import createClient, { type Middleware } from "openapi-fetch";

import { getToken } from "../auth/keycloak";
import { withOpstina } from "./http";
import type { paths } from "./schema";

/** Tipizirani klijent generisan iz OpenAPI (Swagger) specifikacije: `npm run api:gen`. */
export const api = createClient<paths>({ baseUrl: "" });

const auth: Middleware = {
  async onRequest({ request }) {
    const t = await getToken();
    if (t) request.headers.set("Authorization", `Bearer ${t}`);
    // izabrana opština iz zaglavlja filtrira sve podatke i izvještaje
    const u = new URL(request.url, window.location.origin);
    const scoped = withOpstina(u.pathname + u.search);
    if (scoped === u.pathname + u.search) return request;
    // Request sa tijelom (POST/PUT/PATCH) ne može se prepakovati direktno — tijelo se prvo pročita.
    const init: RequestInit = { method: request.method, headers: request.headers, signal: request.signal };
    if (request.method !== "GET" && request.method !== "HEAD") init.body = await request.clone().arrayBuffer();
    return new Request(new URL(scoped, u.origin), init);
  },
};
/** Zaposleni van VPN-a: API vraća 403 {code: "vpn_required"} — aplikacija prikazuje stranu „Uključite VPN“. */
export const VPN_EVENT = "agrisense:vpn-required";
async function checkVpn(r: Response): Promise<void> {
  if (r.status !== 403) return;
  try {
    const b = await r.clone().json();
    if (b?.detail?.code === "vpn_required") window.dispatchEvent(new CustomEvent(VPN_EVENT, { detail: b.detail.message }));
  } catch { /* nije JSON */ }
}
const vpn: Middleware = { async onResponse({ response }) { await checkVpn(response); return response; } };
api.use(auth);
api.use(vpn);

/** fetch sa tokenom — za plocice (MVT/PNG), upload i sve sto nije kroz `api`. */
export async function authFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  // token se šalje SAMO našem serveru — nikad spoljnim geoservisima (sloj bez proxy-ja)
  const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
  const sameOrigin = new URL(url, window.location.href).origin === window.location.origin;
  const t = sameOrigin ? await getToken() : undefined;
  if (t) headers.set("Authorization", `Bearer ${t}`);
  const r = await fetch(input, { ...init, headers });
  if (sameOrigin) await checkVpn(r);
  return r;
}

/** Izvlaci poruku greske iz FastAPI odgovora. */
export function errorMessage(err: unknown): string {
  if (!err) return "Nepoznata greska";
  if (typeof err === "string") return err;
  const d = (err as { detail?: unknown }).detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object" && "message" in d) return String((d as { message: unknown }).message);
  if (Array.isArray(d)) return d.map((x) => `${(x.loc ?? []).join(".")}: ${x.msg}`).join("; ");
  return JSON.stringify(err);
}

/** Baca gresku ako odgovor nije uspjesan, inace vraca data. */
export function unwrap<T>(res: { data?: T; error?: unknown }): T {
  if (res.error !== undefined) throw new Error(errorMessage(res.error));
  return res.data as T;
}

export async function getAuthHeader(): Promise<string | undefined> {
  const t = await getToken();
  return t ? `Bearer ${t}` : undefined;
}
